import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { NotaFiscalEntradaContextoVinculoPedidoResponse } from '@shared/services/responses/orquestrador-compras/nota-fiscal-entrada-contexto-vinculo-pedido.response';
import { NotaFiscalEntradaAssociacaoDialogComponent } from '@shared/components/nota-fiscal-entrada-associacao-dialog/nota-fiscal-entrada-associacao-dialog.component';
import { NotaFiscalEntradaAssociacaoDialogData, NotaFiscalEntradaAssociacaoDialogResult } from '@shared/components/nota-fiscal-entrada-associacao-dialog/nota-fiscal-entrada-associacao-dialog-data';
import { OrigemAssociacaoItensNFeEntradaEnum } from '@shared/enums/suprimentos/origem-associacao-itens-nfe-entrada.enum';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { VinculoPedidoNfDialogComponent } from '../dialogs/vinculo-pedido-nf-dialog/vinculo-pedido-nf-dialog.component';
import { VinculoPedidoNfDialogData, VinculoPedidoNfDialogResult } from '../dialogs/vinculo-pedido-nf-dialog/vinculo-pedido-nf-dialog.data';
import { isItemAssociacaoPendente } from '../processo-compras.associacao';

/**
 * Cadeia de vínculo NF × pedido do processo de compras, comum à etapa Nota do card, ao "Gerenciar
 * vínculo da nota" do quadro e ao modal "Vincular nota fiscal". As travas de edição ficam nos
 * chamadores, porque cada um consulta um conjunto diferente de notas.
 */
@Injectable({ providedIn: 'root' })
export class VinculoNotaPedidoFluxoService {
  constructor(private dialog: MatDialog, private notasService: NotaFiscalEntradaService) {}

  /**
   * Contexto → associação de itens (se algum ainda em associação, `isItemAssociacaoPendente`) → contexto de novo → recusa se houver
   * `bloqueiosVinculo` (aviso com "Abrir a nota") → modal de vínculo (`VinculoPedidoNfDialog`).
   * `true` = vinculou; `false` = o usuário cancelou um passo ou a nota tem bloqueio; erros
   * sobem como `Error` com mensagem amigável.
   */
  async executar(
    idNFeEntrada: number,
    montarIds: (contexto: NotaFiscalEntradaContextoVinculoPedidoResponse) => { idsCompra: number[]; idsVinculoSubstituir: number[] },
  ): Promise<boolean> {
    let contexto = await firstValueFrom(this.notasService.contextoVinculoPedido(idNFeEntrada));
    if (contexto.isError) throw new Error(contexto.errorDescription);
    if (contexto.itens.some(isItemAssociacaoPendente)) {
      const dataAssociacao = new NotaFiscalEntradaAssociacaoDialogData();
      dataAssociacao.idNFeEntrada = idNFeEntrada;
      dataAssociacao.origem = OrigemAssociacaoItensNFeEntradaEnum.KanbanVinculo;
      const associacao = await firstValueFrom(this.dialog.open<NotaFiscalEntradaAssociacaoDialogComponent, NotaFiscalEntradaAssociacaoDialogData, NotaFiscalEntradaAssociacaoDialogResult>(
        NotaFiscalEntradaAssociacaoDialogComponent,
        { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', disableClose: true, autoFocus: false, data: dataAssociacao },
      ).afterClosed());
      // Concluída com divergência bloqueante da conferência segue também: o que impede o vínculo é
      // `bloqueiosVinculo` do contexto recarregado abaixo (a divergência pode ser justamente a falta do
      // pedido). Cancelar, ou só salvar sem concluir, encerra o fluxo.
      if (!associacao?.confirmado && !associacao?.possuiDivergenciaBloqueante) return false;
      contexto = await firstValueFrom(this.notasService.contextoVinculoPedido(idNFeEntrada));
      if (contexto.isError) throw new Error(contexto.errorDescription);
    }
    if (contexto.bloqueiosVinculo?.length) {
      const bloqueio = await firstValueFrom(this.dialog.open(ConfirmModalComponent, {
        data: {
          title: 'Nota com divergência bloqueante',
          description: 'A NF-e não pode ser vinculada a um pedido enquanto houver: ' + contexto.bloqueiosVinculo.join(' '),
          buttonConfirm: 'Abrir a nota',
          buttonClose: 'Fechar',
        },
      }).afterClosed());
      if (bloqueio?.confirm) {
        window.open(`${window.location.origin}/suprimentos/nota-fiscal-entrada/documento/${idNFeEntrada}`, '_blank', 'noopener');
      }
      return false;
    }
    if (contexto.itens.some(isItemAssociacaoPendente)) {
      throw new Error('A NF tem itens com associação pendente. Conclua a associação na Central de NFs.');
    }

    const { idsCompra, idsVinculoSubstituir } = montarIds(contexto);
    const data: VinculoPedidoNfDialogData = { idNFeEntrada, contexto, idsCompra, idsVinculoSubstituir };
    const resultado = await firstValueFrom(this.dialog.open<VinculoPedidoNfDialogComponent, VinculoPedidoNfDialogData, VinculoPedidoNfDialogResult>(
      VinculoPedidoNfDialogComponent,
      { width: '96vw', height: '92vh', maxWidth: '100vw', maxHeight: '100vh', disableClose: true, autoFocus: false, data },
    ).afterClosed());
    return !!resultado?.vinculado;
  }
}
